import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react'

import { useParams } from 'react-router'

import { NavigationSlates } from '@tribeplatform/gql-client/types'
import { SlateRenderer } from '@tribeplatform/react-slate-kit/components'

import { NavbarNavigationItem } from './components/Navbar/constants'
import { useQuery } from './lib/useQuery'

export type MobileSidebarProps = {
  mobileSidebar: ReactElement
  mobileSidebarOpen: boolean
  setMobileSidebarOpen: Dispatch<SetStateAction<boolean>>
  toggleMobileSidebarOpen: () => void
  closeMobileSidebar: () => void
  navigationItems: NavbarNavigationItem[]
  setNavigationItems: (values: NavbarNavigationItem[]) => void
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export const MobileSidebarContext = createContext<MobileSidebarProps>({
  mobileSidebar: null,
  mobileSidebarOpen: false,
  setMobileSidebarOpen: noop,
  closeMobileSidebar: noop,
  toggleMobileSidebarOpen: noop,
  navigationItems: [],
  setNavigationItems: noop,
})

export type MobileSidebarProviderProps = {
  navigationSlates: NavigationSlates
  children: ReactNode
}

export const MobileSidebarProvider = ({
  navigationSlates,
  children,
}: MobileSidebarProviderProps) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState<boolean>(false)
  const closeMobileSidebar = useCallback(() => setMobileSidebarOpen(false), [])
  const toggleMobileSidebarOpen = useCallback(
    () => setMobileSidebarOpen(mobileSidebarOpen => !mobileSidebarOpen),
    [],
  )

  const [navigationItems, setNavigationItems] = useState<
    NavbarNavigationItem[]
  >([])

  const urlParams = useParams()
  const queryParams = useQuery()

  const { sidebar1 } = navigationSlates || {}
  const mobileSidebar = (
    <SlateRenderer
      slate={sidebar1}
      context={{ path: null, urlParams, queryParams }}
    />
  )

  return (
    <MobileSidebarContext.Provider
      value={{
        mobileSidebar,
        mobileSidebarOpen,
        setMobileSidebarOpen,
        toggleMobileSidebarOpen,
        closeMobileSidebar,
        navigationItems,
        setNavigationItems,
      }}
    >
      {children}
    </MobileSidebarContext.Provider>
  )
}

export const useMobileSidebar = (): MobileSidebarProps => {
  const context = useContext(MobileSidebarContext)
  return context
}
